<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div slot="header">
            <i class="fa fa-microchip"></i> Device Management
            <div class="card-header-actions">
              <b-button @click="showCreateModal = true" variant="success" size="sm" class="mr-2">
                <i class="fa fa-plus"></i> Add Device
              </b-button>
              <b-button @click="refreshDevices" variant="outline-primary" size="sm">
                <i class="fa fa-refresh"></i> Refresh
              </b-button>
            </div>
          </div>
          
          <div v-if="loading" class="text-center">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
          
          <div v-else-if="error" class="alert alert-danger">
            <strong>Error:</strong> {{ error.message }}
          </div>
          
          <div v-else>
            <b-table
              :items="devices"
              :fields="fields"
              striped
              hover
              responsive
              :per-page="perPage"
              :current-page="currentPage"
            >
              <template #cell(status)="data">
                <b-badge :variant="data.item.enabled ? 'success' : 'secondary'">
                  {{ data.item.enabled ? 'Active' : 'Inactive' }}
                </b-badge>
              </template>
              
              <template #cell(location)="data">
                <span v-if="data.item.latitude && data.item.longitude">
                  {{ data.item.latitude.toFixed(4) }}, {{ data.item.longitude.toFixed(4) }}
                </span>
                <span v-else class="text-muted">Not set</span>
              </template>
              
              <template #cell(actions)="data">
                <b-button-group size="sm">
                  <b-button 
                    variant="outline-primary" 
                    @click="editDevice(data.item)"
                    :disabled="!data.item.enabled"
                  >
                    <i class="fa fa-edit"></i> Configure
                  </b-button>
                  <b-button 
                    variant="outline-info" 
                    @click="viewDevice(data.item)"
                  >
                    <i class="fa fa-eye"></i> View
                  </b-button>
                </b-button-group>
              </template>
            </b-table>
            
            <b-pagination
              v-if="devices.length > perPage"
              v-model="currentPage"
              :total-rows="devices.length"
              :per-page="perPage"
              align="center"
            ></b-pagination>
          </div>
        </b-card>
      </b-col>
    </b-row>
    
    <!-- Device Configuration Modal -->
    <DeviceConfigModal
      v-if="selectedDevice"
      :device="selectedDevice"
      :visible="showConfigModal"
      @close="closeConfigModal"
      @updated="onDeviceConfigUpdated"
    />

    <!-- Create Device Modal -->
    <b-modal 
      v-model="showCreateModal" 
      title="Add New Device" 
      size="lg"
      @ok="createDevice"
      @cancel="resetCreateForm"
      :ok-disabled="!isCreateFormValid"
    >
      <form @submit.prevent="createDevice">
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Device Name *" label-for="create-name">
              <b-form-input
                id="create-name"
                v-model="createForm.name"
                name="create-name"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-name')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-name')">
                {{ errors.first('create-name') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Device Type *" label-for="create-type">
              <b-form-select
                id="create-type"
                v-model="createForm.type"
                name="create-type"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-type')}"
                :options="deviceTypeOptions"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-type')">
                {{ errors.first('create-type') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Serial Number *" label-for="create-serial">
              <b-form-input
                id="create-serial"
                v-model="createForm.serial"
                name="create-serial"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-serial')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-serial')">
                {{ errors.first('create-serial') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Leaf Serial *" label-for="create-leafSerial">
              <b-form-input
                id="create-leafSerial"
                v-model="createForm.leafSerial"
                name="create-leafSerial"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-leafSerial')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-leafSerial')">
                {{ errors.first('create-leafSerial') }}
              </b-form-text>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Version *" label-for="create-version">
              <b-form-input
                id="create-version"
                v-model="createForm.version"
                name="create-version"
                v-validate="'required'"
                :class="{'is-invalid': errors.has('create-version')}"
                required
              />
              <b-form-text class="is-invalid help" v-show="errors.has('create-version')">
                {{ errors.first('create-version') }}
              </b-form-text>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Subtype" label-for="create-subtype">
              <b-form-input
                id="create-subtype"
                v-model="createForm.subtype"
                name="create-subtype"
              />
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Site" label-for="create-site">
              <b-form-select
                id="create-site"
                v-model="createForm.siteId"
                name="create-site"
                :options="siteOptions"
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Elevation" label-for="create-elevation">
              <b-form-input
                id="create-elevation"
                v-model.number="createForm.elevation"
                name="create-elevation"
                type="number"
                step="0.01"
              />
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Latitude" label-for="create-latitude">
              <b-form-input
                id="create-latitude"
                v-model.number="createForm.latitude"
                name="create-latitude"
                type="number"
                step="0.000001"
                min="-90"
                max="90"
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Longitude" label-for="create-longitude">
              <b-form-input
                id="create-longitude"
                v-model.number="createForm.longitude"
                name="create-longitude"
                type="number"
                step="0.000001"
                min="-180"
                max="180"
              />
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <b-form-group label="Start Date" label-for="create-startDate">
              <b-form-input
                id="create-startDate"
                v-model="createForm.startDate"
                name="create-startDate"
                type="datetime-local"
              />
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="End Date" label-for="create-endDate">
              <b-form-input
                id="create-endDate"
                v-model="createForm.endDate"
                name="create-endDate"
                type="datetime-local"
              />
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <b-form-group>
              <b-form-checkbox v-model="createForm.enabled">
                Enable device immediately
              </b-form-checkbox>
            </b-form-group>
          </div>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { DEVICES_QUERY, CREATE_DEVICE_MUTATION, SITES_QUERY } from '@/constants/graphql'
import { DEVICE_TYPE_OPTIONS } from '@/constants/data'
import DeviceConfigModal from './DeviceConfigModal.vue'

export default {
  name: 'Devices',
  components: {
    DeviceConfigModal
  },
  data () {
    return {
      loading: false,
      error: null,
      selectedDevice: null,
      showConfigModal: false,
      showCreateModal: false,
      currentPage: 1,
      perPage: 10,
      createForm: {
        name: '',
        type: '',
        serial: '',
        leafSerial: '',
        version: '',
        subtype: '',
        siteId: null,
        elevation: null,
        latitude: null,
        longitude: null,
        startDate: '',
        endDate: '',
        enabled: false
      },
      deviceTypeOptions: DEVICE_TYPE_OPTIONS,
      fields: [
        { key: 'name', label: 'Device Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'serial', label: 'Serial', sortable: true },
        { key: 'site.name', label: 'Site', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'location', label: 'Location' },
        { key: 'actions', label: 'Actions', class: 'text-center' }
      ]
    }
  },
  computed: {
    siteOptions () {
      if (!this.sites) return [{ value: null, text: 'Select site...' }]
      return [
        { value: null, text: 'Select site...' },
        ...this.sites.map(site => ({
          value: site.id,
          text: site.name
        }))
      ]
    },
    isCreateFormValid () {
      return this.createForm.name && 
             this.createForm.type && 
             this.createForm.serial && 
             this.createForm.leafSerial && 
             this.createForm.version
    }
  },
  apollo: {
    devices: {
      query: DEVICES_QUERY,
      loadingKey: 'loading',
      error (error) {
        this.error = error
        // eslint-disable-next-line no-console
        console.error('Error loading devices:', error)
      }
    },
    sites: {
      query: SITES_QUERY
    }
  },
  methods: {
    refreshDevices () {
      this.$apollo.queries.devices.refetch()
    },
    editDevice (device) {
      this.selectedDevice = device
      this.showConfigModal = true
    },
    viewDevice (device) {
      // Navigate to device detail view
      this.$router.push({ name: 'DeviceDetail', params: { id: device.id } })
    },
    closeConfigModal () {
      this.showConfigModal = false
      this.selectedDevice = null
    },
    onDeviceConfigUpdated () {
      this.closeConfigModal()
      this.$bvToast.toast('Device configuration updated successfully!', {
        title: 'Success',
        variant: 'success',
        solid: true
      })
    },
    createDevice () {
      this.$validator.validateAll().then((result) => {
        if (result && this.isCreateFormValid) {
          const deviceInput = {
            name: this.createForm.name,
            type: this.createForm.type,
            serial: this.createForm.serial,
            leafSerial: this.createForm.leafSerial,
            version: this.createForm.version,
            enabled: this.createForm.enabled
          }

          // Add optional fields if they have values
          if (this.createForm.subtype) {
            deviceInput.subtype = this.createForm.subtype
          }
          if (this.createForm.elevation !== null && this.createForm.elevation !== '') {
            deviceInput.elevation = parseFloat(this.createForm.elevation)
          }
          if (this.createForm.latitude !== null && this.createForm.latitude !== '') {
            deviceInput.latitude = parseFloat(this.createForm.latitude)
          }
          if (this.createForm.longitude !== null && this.createForm.longitude !== '') {
            deviceInput.longitude = parseFloat(this.createForm.longitude)
          }
          if (this.createForm.startDate) {
            deviceInput.startDate = new Date(this.createForm.startDate).toISOString()
          }
          if (this.createForm.endDate) {
            deviceInput.endDate = new Date(this.createForm.endDate).toISOString()
          }
          if (this.createForm.siteId) {
            deviceInput.site = {
              connect: [{ id: this.createForm.siteId }]
            }
          }

          this.$apollo.mutate({
            mutation: CREATE_DEVICE_MUTATION,
            variables: { input: deviceInput }
          }).then(() => {
            this.$swal.fire({
              icon: 'success',
              title: 'Device created successfully!',
              text: `Device "${this.createForm.name}" has been created.`
            })
            this.showCreateModal = false
            this.resetCreateForm()
            this.$apollo.queries.devices.refetch()
          }).catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Error creating device:', error)
            this.$swal.fire({
              icon: 'error',
              title: 'Error creating device',
              text: error.message || 'An unexpected error occurred'
            })
          })
        }
      })
    },
    resetCreateForm () {
      this.createForm = {
        name: '',
        type: '',
        serial: '',
        leafSerial: '',
        version: '',
        subtype: '',
        siteId: null,
        elevation: null,
        latitude: null,
        longitude: null,
        startDate: '',
        endDate: '',
        enabled: false
      }
      this.$validator.reset()
    }
  }
}
</script>

<style scoped>
.card-header-actions {
  float: right;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
